import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Save, Loader2, Upload, Trash2, FileText, Star, MapPin,
} from 'lucide-react';

import { BACKEND_URL } from '@/config';

const AVAILABLE_FIELDS = [
  { key: 'learner_name', label: 'Learner Name', placeholder: '{{learner_name}}' },
  { key: 'course_title', label: 'Course Title', placeholder: '{{course_title}}' },
  { key: 'completion_date', label: 'Completion Date', placeholder: '{{completion_date}}' },
  { key: 'certificate_number', label: 'Certificate Number', placeholder: '{{certificate_number}}' },
  { key: 'qr_code', label: 'QR Code', placeholder: '{{qr_code}}' },
  { key: 'organisation_name', label: 'Organisation Name', placeholder: '{{organisation_name}}' },
  { key: 'instructor_name', label: 'Instructor Name', placeholder: '{{instructor_name}}' },
];

const CertificateTemplateManager = () => {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/certificates/templates/list');
      setTemplates(res.data || []);
      if (res.data?.length > 0 && !selected) {
        setSelected(res.data[0]);
      }
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const res = await api.post('/certificates/templates', { name: newName.trim() });
      toast.success('Template created');
      setShowCreate(false);
      setNewName('');
      await fetchTemplates();
      setSelected(res.data);
    } catch { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  const handleUploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/certificates/templates/${selected.id}/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelected(p => ({ ...p, pdf_template_url: res.data.pdf_template_url }));
      toast.success('Template file uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const updateFieldPosition = (fieldKey, prop, value) => {
    setSelected(prev => {
      const positions = { ...(prev.field_positions || {}) };
      if (!positions[fieldKey]) {
        positions[fieldKey] = { x: 0, y: 0, font_size: 14, font_color: '#000000', alignment: 'left' };
      }
      positions[fieldKey] = { ...positions[fieldKey], [prop]: prop === 'font_color' || prop === 'alignment' ? value : Number(value) };
      return { ...prev, field_positions: positions };
    });
  };

  const removeField = (fieldKey) => {
    setSelected(prev => {
      const positions = { ...(prev.field_positions || {}) };
      delete positions[fieldKey];
      return { ...prev, field_positions: positions };
    });
  };

  const addField = (fieldKey) => {
    setSelected(prev => ({
      ...prev,
      field_positions: {
        ...(prev.field_positions || {}),
        [fieldKey]: { x: 100, y: 100, font_size: 14, font_color: '#000000', alignment: 'center' },
      },
    }));
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      // Serialize field_positions properly
      const payload = {
        name: selected.name,
        primary_color: selected.primary_color,
        font_family: selected.font_family,
        field_positions: selected.field_positions || {},
      };
      await api.put(`/certificates/templates/${selected.id}`, payload);
      toast.success('Template saved');
      fetchTemplates();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSetDefault = async (id) => {
    try {
      await api.post(`/certificates/templates/${id}/set-default`);
      toast.success('Default template set');
      fetchTemplates();
    } catch { toast.error('Failed to set default'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/certificates/templates/${id}`);
      toast.success('Deleted');
      if (selected?.id === id) setSelected(null);
      fetchTemplates();
    } catch { toast.error('Delete failed'); }
  };

  const activeFields = selected ? Object.keys(selected.field_positions || {}) : [];
  const availableToAdd = AVAILABLE_FIELDS.filter(f => !activeFields.includes(f.key));

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="space-y-6" data-testid="cert-template-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Certificate Templates</h1>
            <p className="text-slate-500 mt-1 text-sm">Manage PDF templates and field positions</p>
          </div>
          <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={() => setShowCreate(true)} data-testid="create-template-btn">
            <Plus className="h-4 w-4 mr-2" /> New Template
          </Button>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Template List */}
          <div className="space-y-2">
            {templates.map(t => (
              <Card
                key={t.id}
                className={`cursor-pointer border-2 transition-colors ${selected?.id === t.id ? 'border-[#20acf6] bg-[#20acf6]/5' : 'border-slate-200 hover:border-slate-300'}`}
                onClick={() => setSelected(t)}
                data-testid={`template-card-${t.id}`}
              >
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-slate-800">{t.name}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      {t.is_default && <Badge className="bg-[#02d1d4] text-white text-[9px]">Default</Badge>}
                      {t.pdf_template_url && <Badge variant="outline" className="text-[9px]">PDF</Badge>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {!t.is_default && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleSetDefault(t.id); }} title="Set as default">
                        <Star className="h-3.5 w-3.5 text-slate-400" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleDelete(t.id); }}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {templates.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">No templates yet. Create one to get started.</p>
            )}
          </div>

          {/* Template Editor */}
          {selected ? (
            <div className="space-y-5">
              {/* Template Info */}
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Template Settings</CardTitle>
                  <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={selected.name}
                        onChange={e => setSelected(p => ({ ...p, name: e.target.value }))}
                        data-testid="template-name-input"
                      />
                    </div>
                    <div>
                      <Label>Font Family</Label>
                      <Select value={selected.font_family || 'IBM Plex Sans'} onValueChange={v => setSelected(p => ({ ...p, font_family: v }))}>
                        <SelectTrigger data-testid="template-font-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="IBM Plex Sans">IBM Plex Sans</SelectItem>
                          <SelectItem value="Arial">Arial</SelectItem>
                          <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                          <SelectItem value="Georgia">Georgia</SelectItem>
                          <SelectItem value="Courier New">Courier New</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Primary Colour</Label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={selected.primary_color || '#2a3e5d'}
                          onChange={e => setSelected(p => ({ ...p, primary_color: e.target.value }))}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input
                          value={selected.primary_color || '#2a3e5d'}
                          onChange={e => setSelected(p => ({ ...p, primary_color: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* PDF Upload */}
                  <div>
                    <Label className="mb-2 block">PDF Template File</Label>
                    {selected.pdf_template_url ? (
                      <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                        <FileText className="h-8 w-8 text-[#20acf6]" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-700">Template uploaded</p>
                          <a
                            href={`${BACKEND_URL}${selected.pdf_template_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-[#20acf6] hover:underline"
                          >
                            View file
                          </a>
                        </div>
                        <label className="cursor-pointer">
                          <Button asChild variant="outline" size="sm">
                            <span>{uploading ? 'Uploading...' : 'Replace'}</span>
                          </Button>
                          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" onChange={handleUploadPdf} />
                        </label>
                      </div>
                    ) : (
                      <label className="cursor-pointer block" data-testid="upload-pdf-btn">
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#20acf6] transition-colors">
                          <Upload className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-500">
                            {uploading ? 'Uploading...' : 'Click to upload PDF or image template'}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">Supported: PDF, PNG, JPG</p>
                        </div>
                        <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" onChange={handleUploadPdf} />
                      </label>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Field Positions */}
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-[#20acf6]" /> Field Positions (x, y coordinates)
                  </CardTitle>
                  {availableToAdd.length > 0 && (
                    <Select onValueChange={addField}>
                      <SelectTrigger className="w-48" data-testid="add-field-select">
                        <SelectValue placeholder="Add field..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableToAdd.map(f => (
                          <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {activeFields.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6">
                      No fields configured. Use the dropdown above to add fields.
                    </p>
                  ) : (
                    activeFields.map(fieldKey => {
                      const field = AVAILABLE_FIELDS.find(f => f.key === fieldKey);
                      const pos = selected.field_positions?.[fieldKey] || {};
                      return (
                        <div
                          key={fieldKey}
                          className="bg-slate-50 rounded-lg p-4 space-y-3"
                          data-testid={`field-position-${fieldKey}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-sm text-slate-800">{field?.label || fieldKey}</span>
                              <code className="ml-2 text-xs text-slate-400 bg-slate-200 px-1.5 py-0.5 rounded">
                                {field?.placeholder || `{{${fieldKey}}}`}
                              </code>
                            </div>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeField(fieldKey)}>
                              <Trash2 className="h-3.5 w-3.5 text-red-400" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            <div>
                              <Label className="text-xs">X (px)</Label>
                              <Input
                                type="number"
                                value={pos.x || 0}
                                onChange={e => updateFieldPosition(fieldKey, 'x', e.target.value)}
                                data-testid={`field-${fieldKey}-x`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Y (px)</Label>
                              <Input
                                type="number"
                                value={pos.y || 0}
                                onChange={e => updateFieldPosition(fieldKey, 'y', e.target.value)}
                                data-testid={`field-${fieldKey}-y`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Font Size</Label>
                              <Input
                                type="number"
                                value={pos.font_size || 14}
                                onChange={e => updateFieldPosition(fieldKey, 'font_size', e.target.value)}
                                data-testid={`field-${fieldKey}-size`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Colour</Label>
                              <div className="flex gap-1">
                                <input
                                  type="color"
                                  value={pos.font_color || '#000000'}
                                  onChange={e => updateFieldPosition(fieldKey, 'font_color', e.target.value)}
                                  className="w-10 h-10 rounded border cursor-pointer"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Align</Label>
                              <Select value={pos.alignment || 'left'} onValueChange={v => updateFieldPosition(fieldKey, 'alignment', v)}>
                                <SelectTrigger className="h-10">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="left">Left</SelectItem>
                                  <SelectItem value="center">Center</SelectItem>
                                  <SelectItem value="right">Right</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center py-20 text-slate-400">
              <div className="text-center">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Select a template or create a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-template-dialog">
          <DialogHeader><DialogTitle>Create Certificate Template</DialogTitle></DialogHeader>
          <div>
            <Label>Template Name</Label>
            <Input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Standard Certificate"
              data-testid="new-template-name"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={handleCreate} disabled={saving} data-testid="create-template-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CertificateTemplateManager;
